
/* Siya Jewel — native Product Details and Shipping tabs, v2.1 */
(() => {
  'use strict';
  if (customElements.get('siya-native-tabs')) return;

  class SiyaNativeTabs extends HTMLElement {
    connectedCallback() {
      if (!this._initialized) {
        this._initialized = true;
        this.tabs = Array.from(this.querySelectorAll('[data-sjnt-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-sjnt-panel]'));
        this.expandables = Array.from(this.querySelectorAll('[data-sjnt-expandable]')).map(wrapper => {
          const item = {
            wrapper,
            body: wrapper.querySelector('[data-sjnt-expand-body]'),
            measure: wrapper.querySelector('[data-sjnt-expand-measure]'),
            button: wrapper.querySelector('[data-sjnt-readmore]'),
            expanded: false,
            originalTabIndexes: new Map()
          };
          item.button.addEventListener('click', () => this.toggleMore(item));
          return item;
        });

        this.addEventListener('click', event => {
          const tab = event.target.closest('[data-sjnt-tab]');
          if (tab && this.contains(tab)) this.activate(tab.dataset.sjntTab, false);
        });

        this.addEventListener('keydown', event => {
          const tab = event.target.closest('[data-sjnt-tab]');
          if (!tab || !this.contains(tab)) return;
          const index = this.tabs.indexOf(tab);
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          this.activate(this.tabs[next].dataset.sjntTab, true);
        });

        this.addEventListener('shopify:block:select', () => {
          this.activate(this.dataset.initialTab === 'shipping' ? 'shipping' : 'details', false);
        });

        this._onResize = () => this.refreshAll();
        this._onLoad = () => { if (this.isConnected) this.refreshAll(); };
      }

      if (!this._connected) {
        this._connected = true;
        window.addEventListener('resize', this._onResize);
        window.addEventListener('load', this._onLoad, {once: true});
        if (typeof ResizeObserver !== 'undefined') {
          this._resizeObserver = new ResizeObserver(() => this.refreshAll());
          this.expandables.forEach(item => this._resizeObserver.observe(item.measure));
        }
      }

      this.activate(this.dataset.initialTab === 'shipping' ? 'shipping' : 'details', false);
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onLoad);
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    activate(name, focus) {
      if (name !== 'details' && name !== 'shipping') return;
      this.tabs.forEach(tab => {
        const active = tab.dataset.sjntTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      this.panels.forEach(panel => {
        panel.hidden = panel.dataset.sjntPanel !== name;
      });
      this.refreshAll();
    }

    refreshAll() {
      this.expandables.forEach(item => this.refreshMore(item));
    }

    setFocusable(item, enabled) {
      const selector = 'a[href], button, input, select, textarea, [tabindex]';
      if (!enabled && item.body.contains(document.activeElement)) {
        item.button.focus();
      }
      item.body.querySelectorAll(selector).forEach(element => {
        if (!enabled) {
          if (!item.originalTabIndexes.has(element)) {
            item.originalTabIndexes.set(element, element.getAttribute('tabindex'));
          }
          element.setAttribute('tabindex', '-1');
        } else if (item.originalTabIndexes.has(element)) {
          const original = item.originalTabIndexes.get(element);
          if (original === null) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', original);
          item.originalTabIndexes.delete(element);
        }
      });
    }

    refreshMore(item) {
      // Hidden tab panels have no measurable layout; measure on activation.
      if (!item.body.getClientRects().length) return;

      const enabled = item.wrapper.dataset.enabled === 'true';
      const preview = Math.max(80, Number(item.wrapper.dataset.previewHeight) || 180);
      const natural = Math.ceil(item.measure.getBoundingClientRect().height);
      const overflow = enabled && natural > preview + 2;

      if (!overflow) {
        item.expanded = false;
        item.wrapper.classList.remove('is-collapsed', 'is-expanded');
        item.body.style.removeProperty('max-height');
        item.button.hidden = true;
        item.button.setAttribute('aria-expanded', 'false');
        this.setFocusable(item, true);
        return;
      }

      item.button.hidden = false;
      item.wrapper.classList.toggle('is-expanded', item.expanded);
      item.wrapper.classList.toggle('is-collapsed', !item.expanded);
      item.button.setAttribute('aria-expanded', String(item.expanded));
      if (item.expanded) {
        item.body.style.removeProperty('max-height');
        this.setFocusable(item, true);
      } else {
        item.body.style.maxHeight = preview + 'px';
        this.setFocusable(item, false);
      }
    }

    toggleMore(item) {
      item.expanded = !item.expanded;
      this.refreshMore(item);
    }
  }

  customElements.define('siya-native-tabs', SiyaNativeTabs);
})();